import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { raClient, type RAListParams, type RAListResponse } from '@/lib/raClient'

export type WhatsAppRegistrationStatus = 'pending' | 'online' | 'offline' | 'failed'

export interface PhoneNumber {
  id: number
  number: string
  created_at?: string
  updated_at?: string
  assigned_assistant_id?: number | null
  assigned_assistant_name?: string | null
  waba_id?: string | null
  meta_phone_number_id?: string | null
  twilio_subaccount_sid?: string | null
  messaging_service_sid?: string | null
  twilio_sender_sid?: string | null
  whatsapp_status?: WhatsAppRegistrationStatus | null
}

// Query key factory
export const phoneNumberKeys = {
  all: ['phone-numbers'] as const,
  lists: () => [...phoneNumberKeys.all, 'list'] as const,
  list: (search?: string, assistantId?: number) =>
    [...phoneNumberKeys.lists(), { search, assistantId }] as const,
  adminList: (params?: RAListParams) =>
    [...phoneNumberKeys.all, 'admin-list', params] as const,
  details: () => [...phoneNumberKeys.all, 'detail'] as const,
  detail: (id: number) => [...phoneNumberKeys.details(), id] as const,
}

interface UsePhoneNumbersListOptions {
  search?: string
  assistantId?: number
  enabled?: boolean
}

/**
 * Hook to fetch list of available phone numbers for autocomplete
 */
export function usePhoneNumbersList({
  search = '',
  assistantId,
  enabled = true,
}: UsePhoneNumbersListOptions = {}) {
  return useQuery({
    queryKey: phoneNumberKeys.list(search, assistantId),
    queryFn: async (): Promise<PhoneNumber[]> => {
      const params = new URLSearchParams()
      params.set('sort', JSON.stringify(['number', 'ASC']))
      params.set('range_', JSON.stringify([0, 50]))

      // Build filter
      const filter: Record<string, unknown> = {}
      if (search) {
        filter.number = `%${search}%`
      }
      if (assistantId) {
        filter.assistant_id = assistantId
      }
      params.set('filter', JSON.stringify(filter))

      const response = await api.post(`/phone-numbers/ra/getList?${params.toString()}`)
      return response.data.data || response.data
    },
    enabled,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Hook to fetch a single phone number by ID
 */
export function usePhoneNumber(id: number | null | undefined) {
  return useQuery({
    queryKey: phoneNumberKeys.detail(id!),
    queryFn: async (): Promise<PhoneNumber> => {
      const response = await api.post(`/phone-numbers/ra/getOne/${id}`)
      return response.data.data || response.data
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch ALL phone numbers (admin page) with pagination
 * Uses show_all filter to bypass the unassigned-only default
 */
export function usePhoneNumbersAdmin(params: RAListParams = {}) {
  const mergedParams: RAListParams = {
    ...params,
    filter: { ...params.filter, show_all: true },
  }

  return useQuery({
    queryKey: phoneNumberKeys.adminList(mergedParams),
    queryFn: () => raClient.getList<PhoneNumber>('phone-numbers', mergedParams),
    staleTime: 30 * 1000,
  })
}

/**
 * Hook to create a new phone number
 */
export function useCreatePhoneNumber() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { number: string }) =>
      raClient.create<PhoneNumber>('phone-numbers', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.all })
    },
  })
}

/**
 * Hook to delete a phone number (soft delete)
 */
export function useDeletePhoneNumber() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => raClient.delete('phone-numbers', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.all })
    },
  })
}
